import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";

import './App.scss';
import './styles/_colors.scss';
import './styles/alerts-style.scss';
import './styles/utilities.scss';
import './styles/buttons.scss';

import { LoginPage } from './components/pages/Auth/LoginPage';
import RegisterPage from './components/pages/Auth/RegisterPage';
import ForgotPasswordPage from './components/pages/Auth/ForgotPasswordPage';
import ResetPasswordPage from './components/pages/Auth/ResetPasswordPage';
import SsoCallbackPage from './components/pages/Auth/SsoCallbackPage';
import PrivacyPage from './components/pages/PrivacyPage';
import LegalFoursquareOsPlacesPage from './components/pages/LegalFoursquareOsPlacesPage';
import AccountDeletionRecoveryPage from './components/pages/AccountDeletion/RecoveryPage';
import AccountDeletionRestrictedPage from './components/pages/AccountDeletion/RestrictedPage';
import AccountDeletionSsoCallbackPage from './components/pages/AccountDeletion/SsoCallbackPage';
import AccountDeletionStatusPage from './components/pages/AccountDeletion/StatusPage';
import PublicBandPage from './components/pages/PublicBandPage';
import PublicProfilePage from './components/pages/PublicProfilePage';
import { Context } from './store/context';
import { UnreadMessagesProvider } from './store/UnreadMessagesContext';
import { PresenceProvider } from './store/PresenceContext';
import { StompProvider } from './store/StompContext';
import { BackgroundVariantProvider } from './store/BackgroundVariantContext';
import { UserPreferencesProvider } from "./store/UserPreferencesContext";
import { TrackPlaybackProvider } from "./store/TrackPlaybackContext";
import { MainPage } from './components/pages/MainPage';
import PrivateRoute from "./components/security/PrivateRoute";
import { fetchCurrentUser } from "./services/auth";
import LoadingSpinner from './components/atoms/LoadingSpinner';
import UnreadWatcher from './components/organisms/Background/UnreadWatcher';
import PresenceHeartbeat from './components/organisms/Background/PresenceHeartbeat';
import UnreadBootstrap from './components/organisms/Background/UnreadBootstrap';
import ProfilePreviewProvider from './components/organisms/ProfilePreviewProvider';
import GlobalTrackMiniPlayer from './components/molecules/GlobalTrackMiniPlayer';
import { AUTH_SESSION_EXPIRED_EVENT } from "./services/authRefreshCoordinator";
import { clearUserScopedClientState } from "./services/clientSessionLifecycle";
import { getProfileRouteLocator } from "./utils/profileRoutes";
import {
    ACCOUNT_DELETION_ROUTES,
    isAccountDeletionPublicPath,
} from "./constants/accountDeletionRoutes";
import { isAdminBillingUiEnabled, isBillingUiEnabled } from "./constants/billingFeatures";

function ProfileRouteCatchAll() {
    const location = useLocation();

    if (!getProfileRouteLocator(location.pathname)) {
        return <Navigate to="/" replace />;
    }

    return (
        <PrivateRoute>
            <MainPage />
        </PrivateRoute>
    );
}

function ProfileUsernameRoute({ isLoggedIn }) {
    const { profileUsername } = useParams();
    if (typeof profileUsername !== "string" || !profileUsername.startsWith("@")) {
        return <Navigate to="/" replace />;
    }

    return isLoggedIn ? (
        <PrivateRoute>
            <MainPage />
        </PrivateRoute>
    ) : <PublicProfilePage />;
}

function BandShareRoute({ isLoggedIn }) {
    const { bandHandle } = useParams();
    const location = useLocation();
    const isSharedBandHandle = typeof bandHandle === "string" && bandHandle.startsWith("@");

    if (isLoggedIn) {
        return (
            <PrivateRoute>
                <MainPage />
            </PrivateRoute>
        );
    }

    return isSharedBandHandle ? <PublicBandPage /> : (
        <Navigate
            to="/login"
            replace
            state={{ from: `${location.pathname}${location.search}${location.hash}` }}
        />
    );
}

function AuthenticatedApp() {
    const dispatch = useDispatch();
    const { user: currentUser, isLoggedIn } = useSelector((state) => state.auth);
    const [isLoading, setIsLoading] = useState(true);
    const [isContentVisible, setIsContentVisible] = useState(false);

    useEffect(() => {
        dispatch(fetchCurrentUser())
            .catch(() => <Navigate to="/login" />)
            .finally(() => {
                setIsLoading(false);
                // Add a small delay before showing content for smooth transition
                setTimeout(() => setIsContentVisible(true), 100);
            });
    }, [dispatch]);

    useEffect(() => {
        const onAuthSessionExpired = () => {
            void clearUserScopedClientState({ dispatch, broadcast: false });
        };
        window.addEventListener(AUTH_SESSION_EXPIRED_EVENT, onAuthSessionExpired);
        return () => {
            window.removeEventListener(AUTH_SESSION_EXPIRED_EVENT, onAuthSessionExpired);
        };
    }, [dispatch]);

    // Utility for rendering private routes
    const renderPrivateRoute = (path, Component, routeOptions = {}) => (
        <Route
            path={path}
            element={
                <PrivateRoute requiredRoles={routeOptions.requiredRoles}>
                    <Component />
                </PrivateRoute>
            }
        />
    );

    return (
        <Context.Provider value={currentUser}>
            <StompProvider enabled={isLoggedIn}>
                <PresenceProvider>
                    <UnreadMessagesProvider>
                        <UserPreferencesProvider>
                            <BackgroundVariantProvider>
                            {isLoading ? (
                                <div
                                    style={{
                                        position: 'fixed',
                                        inset: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        background: 'rgba(0, 0, 0, 0.5)',
                                        color: '#e6edff',
                                        zIndex: 9999,
                                    }}
                                >
                                    <LoadingSpinner size={40} />
                                </div>
                            ) : (
                                // <AppBackground /> IT IS SO GLITCHY
                                <div className={`app-shell ${isContentVisible ? 'fade-in' : 'hidden'}`}>
                                    <TrackPlaybackProvider>
                                        <ProfilePreviewProvider>
                                            <PresenceHeartbeat />
                                            <UnreadWatcher />
                                            <UnreadBootstrap />
                                            <Routes>
                                                {/* Public routes */}
                                                <Route path="/login" element={isLoggedIn ? <Navigate to="/" /> : <LoginPage />} />
                                                <Route path="/register" element={isLoggedIn ? <Navigate to="/" /> : <RegisterPage />} />
                                                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                                                <Route path="/reset-password" element={<ResetPasswordPage />} />
                                                <Route path="/sso/:provider/callback" element={<SsoCallbackPage />} />
                                                <Route path="/privacy" element={<PrivacyPage />} />
                                                <Route path="/legal/foursquare-os-places" element={<LegalFoursquareOsPlacesPage />} />
                                                <Route path="/:profileUsername" element={<ProfileUsernameRoute isLoggedIn={isLoggedIn} />} />
                                                <Route path="/bands/:bandHandle" element={<BandShareRoute isLoggedIn={isLoggedIn} />} />

                                                {/* Protected routes */}
                                                {renderPrivateRoute("/", MainPage)}
                                                {renderPrivateRoute("/profile/:profileUserId", MainPage)}
                                                {renderPrivateRoute("/discover", MainPage)}
                                                {renderPrivateRoute("/events", MainPage)}
                                                {renderPrivateRoute("/venues", MainPage)}
                                                {renderPrivateRoute("/bands", MainPage)}
                                                {renderPrivateRoute("/bands/:bandObjectId/:bandSlug", MainPage)}
                                                {isBillingUiEnabled()
                                                    ? renderPrivateRoute("/billing", MainPage)
                                                    : <Route path="/billing" element={<Navigate to="/" replace />} />}
                                                {isAdminBillingUiEnabled()
                                                    ? <Route path="/admin/billing" element={<Navigate to="/system?section=billing" replace />} />
                                                    : <Route path="/admin/billing" element={<Navigate to="/" replace />} />}
                                                {renderPrivateRoute("/system", MainPage, { requiredRoles: ["ROLE_ADMIN"] })}

                                                {/* Catch-all route */}
                                                <Route path="*" element={<ProfileRouteCatchAll />} />
                                            </Routes>
                                            <GlobalTrackMiniPlayer />
                                        </ProfilePreviewProvider>
                                    </TrackPlaybackProvider>
                                </div>
                            )}
                            </BackgroundVariantProvider>
                        </UserPreferencesProvider>
                    </UnreadMessagesProvider>
                </PresenceProvider>
            </StompProvider>
        </Context.Provider>
    );
}

function App() {
    const location = useLocation();

    if (isAccountDeletionPublicPath(location.pathname)) {
        return (
            <Routes>
                <Route path={ACCOUNT_DELETION_ROUTES.authorize} element={<AccountDeletionSsoCallbackPage />} />
                <Route path={ACCOUNT_DELETION_ROUTES.recovery} element={<AccountDeletionRecoveryPage />} />
                <Route path={ACCOUNT_DELETION_ROUTES.restricted} element={<AccountDeletionRestrictedPage />} />
                <Route path={ACCOUNT_DELETION_ROUTES.status} element={<AccountDeletionStatusPage />} />
            </Routes>
        );
    }

    return <AuthenticatedApp />;
}

export default App;
